import type {
    DinoLanguage,
    DinoTutorExerciseType
} from "../../features/mascot/context/index.js";
import type {
    HintEngine,
    TutorHint,
    TutorHintRequest
} from "./HintEngine.js";

interface HintStrategyCopy {
    readonly summary: string;
    readonly nextStep: string;
}

const titleByLanguage = {
    fr: "Un indice, pas la réponse",
    fa: "یک راهنمایی، نه پاسخ"
} as const;

const objectivePrefixByLanguage = {
    fr: "Objectif",
    fa: "هدف"
} as const;

const fallbackObjectiveByLanguage = {
    fr: "Résoudre l’exercice étape par étape",
    fa: "حل تمرین به‌صورت مرحله‌به‌مرحله"
} as const;

const strategies: Record<
    DinoLanguage,
    Record<DinoTutorExerciseType, HintStrategyCopy>
> = {
    fr: {
        mcq: {
            summary: "Repère dans la consigne le mot ou le groupe qui porte la règle, puis élimine les choix qui ne respectent pas ce repère.",
            nextStep: "Compare ensuite les choix restants un par un, sans chercher à tout décider en même temps."
        },
        binary: {
            summary: "Vérifie une seule règle dans l’affirmation avant de décider si elle est vraie ou fausse.",
            nextStep: "Cherche un contre-exemple : s’il existe, l’affirmation ne peut pas être toujours vraie."
        },
        fill_blank: {
            summary: "Observe les mots autour du blanc : ils indiquent souvent la personne, le temps, le genre ou le nombre attendu.",
            nextStep: "Relis ensuite la phrase entière avec ta proposition pour vérifier qu’elle reste naturelle."
        },
        ordering: {
            summary: "Construis d’abord le noyau de la phrase, puis ajoute les compléments autour de lui.",
            nextStep: "Cherche le sujet et le verbe avant de replacer les groupes secondaires."
        }
    },
    fa: {
        mcq: {
            summary: "در صورت سؤال، واژه یا گروهی را پیدا کن که قاعده به آن وابسته است؛ سپس گزینه‌های ناسازگار را کنار بگذار.",
            nextStep: "گزینه‌های باقی‌مانده را یکی‌یکی مقایسه کن و همه‌چیز را هم‌زمان تصمیم نگیر."
        },
        binary: {
            summary: "پیش از انتخاب درست یا نادرست، فقط یک قاعده را در جمله بررسی کن.",
            nextStep: "یک مثال نقض پیدا کن؛ اگر وجود داشته باشد، جمله نمی‌تواند همیشه درست باشد."
        },
        fill_blank: {
            summary: "به واژه‌های اطراف جای خالی نگاه کن؛ آن‌ها معمولاً شخص، زمان، جنس یا شمار موردنیاز را نشان می‌دهند.",
            nextStep: "سپس جمله را با پیشنهاد خودت کامل بخوان و طبیعی‌بودن آن را بررسی کن."
        },
        ordering: {
            summary: "ابتدا هستهٔ جمله را بساز و بعد بخش‌های تکمیلی را در جای مناسب قرار بده.",
            nextStep: "پیش از مرتب‌کردن گروه‌های فرعی، فاعل و فعل را پیدا کن."
        }
    }
};

class GuardedHintEngine implements HintEngine {
    async hint(request: TutorHintRequest): Promise<TutorHint> {
        const strategy = strategies[request.language][request.exercise.type];
        const objective = request.exercise.objective.trim()
            || fallbackObjectiveByLanguage[request.language];

        return {
            mode: "guarded-hint",
            title: titleByLanguage[request.language],
            summary: strategy.summary,
            points: [
                `${objectivePrefixByLanguage[request.language]} : ${objective}`,
                strategy.nextStep
            ],
            sourceLabel: request.exercise.prompt.trim() || objective
        };
    }
}

const guardedHintEngine = new GuardedHintEngine();

export {
    GuardedHintEngine,
    guardedHintEngine
};
